import { useMemo } from 'react'

import { useSession } from '@clerk/nextjs'
import * as Dropdown from '@radix-ui/react-dropdown-menu'
import { ExternalLinkIcon } from '@radix-ui/react-icons'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useQuery } from 'urql'

import useLogout from 'domains/user/use-logout'
import { dashboardPaths, websitePaths } from 'domains/website/website.paths'
import { ViewerDocument } from 'graphql/schema/user'
import { useAnimation } from 'store/animations.state'

import Avatar from 'components/atomic/atoms/avatar'
import Box from 'components/atomic/atoms/box'
import { IconStyled } from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'
import Feedback from 'components/pages/@shared/feedback'
import {
  ContentStyled,
  ItemStyled,
  SeparatorStyled,
} from 'components/pages/@shared/header-user-dropdown/header-user-dropdown.styled'
import ThemeSelect from 'components/pages/@shared/theme-select'

const SharedHeaderUserDropdown = () => {
  const { logout } = useLogout()
  const { query, asPath } = useRouter()
  const { session } = useSession()
  const { animation } = useAnimation({ key: 'user-navbar' })

  const isDashboard = !!query.account || asPath === '/dashboard'

  const [{ data: dataViewer }] = useQuery({
    query: ViewerDocument,
    requestPolicy: 'cache-first',
  })

  const name = useMemo(() => {
    if (dataViewer?.viewer?.name) {
      return dataViewer?.viewer?.name
    }

    if (dataViewer?.viewer?.email) {
      return dataViewer?.viewer?.email
    }

    if (session?.user.firstName && session?.user.lastName) {
      return `${session.user.firstName} ${session.user.lastName}`
    }

    if (session?.user.firstName) {
      return session.user.firstName
    }

    if (session?.user?.primaryEmailAddress?.emailAddress) {
      return session.user.primaryEmailAddress.emailAddress
    }

    return null
  }, [dataViewer, session])

  const imageUrl = useMemo(() => {
    return session?.user.externalAccounts.find(a => a.provider === 'github')
      ?.imageUrl
  }, [session])

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger asChild>
        {session?.user ? (
          <Box
            flex
            as="button"
            items="center"
            css={{
              animation,
              color: '$text2',
              spaceX: 1,
              borderRadius: '$md',
              '&:hover': {
                color: '$text1',
              },
              '&:focus': {
                outline: 'none',
              },
            }}
          >
            <Avatar src={imageUrl} name={name} />
          </Box>
        ) : (
          <Box as="button" disabled css={{ width: 44, opacity: 0 }} />
        )}
      </Dropdown.Trigger>
      <ContentStyled sideOffset={8} align="end">
        <Box flex="column" css={{ px: '$4', mb: '$2' }}>
          <Text size="sm">{name}</Text>
          {!!name && (
            <Text size="xs" color="text2">
              {session?.user?.primaryEmailAddress?.emailAddress}
            </Text>
          )}
        </Box>
        <SeparatorStyled />
        <Link href={dashboardPaths.settings}>
          <ItemStyled>Settings</ItemStyled>
        </Link>
        <Box flex items="center" css={{ mx: '$4', my: '$2', spaceX: 3 }}>
          <Text size="sm">Theme</Text>
          <ThemeSelect size="sm" />
        </Box>
        <Box css={{ display: 'block', '@sm': { display: 'none' } }}>
          <Feedback
            trigger={
              <Box as="button" css={{ height: 32, px: '$2', mx: '$2' }}>
                Feedback
              </Box>
            }
          />
          <Link href={websitePaths.changelog}>
            <ItemStyled>Changelog</ItemStyled>
          </Link>
        </Box>
        {isDashboard && (
          <>
            <SeparatorStyled />
            <Box as="a" href={websitePaths.root} target="_blank">
              <Box
                flex
                justify="between"
                items="center"
                as={ItemStyled}
                targetChildren={false}
                css={{ '&:hover svg': { color: '$text1' } }}
              >
                <Box>Homepage</Box>
                <Box css={{ mr: '$4' }}>
                  <IconStyled as={ExternalLinkIcon} color="text2" />
                </Box>
              </Box>
            </Box>
          </>
        )}
        <SeparatorStyled />
        <ItemStyled onSelect={logout}>Sign Out</ItemStyled>
      </ContentStyled>
    </Dropdown.Root>
  )
}

export default SharedHeaderUserDropdown
